'use client';

import type { ComponentProps, ReactNode } from 'react';

import { cn } from '@/lib/ui/utils';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from './tooltip';

type UnifiedTooltipImage = {
  src: string;
  alt: string;
  className?: string;
  height?: number;
  width?: number;
};

type UnifiedTooltipProps = Omit<
  ComponentProps<typeof Tooltip>,
  'children'
> &
  Omit<
    ComponentProps<typeof TooltipContent>,
    'children' | 'className' | 'content'
  > & {
    bodyClassName?: string;
    content?: ReactNode;
    contentClassName?: string;
    description?: ReactNode;
    image?: UnifiedTooltipImage;
    text?: ReactNode;
    title?: ReactNode;
    trigger: ReactNode;
    triggerAsChild?: boolean;
  };

export function UnifiedTooltip({
  align = 'center',
  bodyClassName,
  content,
  contentClassName,
  description,
  image,
  side = 'top',
  sideOffset = 8,
  text,
  title,
  trigger,
  triggerAsChild = false,
  ...tooltipProps
}: UnifiedTooltipProps) {
  const hasStructuredContent = Boolean(image || title || description || text);
  const resolvedContent =
    content ??
    (hasStructuredContent ? (
      <div
        className={cn(
          'flex max-w-[18rem] flex-col gap-2 text-xs leading-5',
          bodyClassName
        )}
      >
        {image ? (
          <div className="overflow-hidden rounded-control bg-background/8">
            <img
              src={image.src}
              alt={image.alt}
              width={image.width}
              height={image.height}
              className={cn(
                'block max-h-56 w-full object-cover',
                image.className
              )}
            />
          </div>
        ) : null}
        {title || description || text ? (
          <div className="space-y-1">
            {title ? (
              <div className="text-sm font-semibold leading-5">{title}</div>
            ) : null}
            {description ? (
              <div className="text-background/80">{description}</div>
            ) : null}
            {text ? <div>{text}</div> : null}
          </div>
        ) : null}
      </div>
    ) : null);

  if (!resolvedContent) {
    return <>{trigger}</>;
  }

  return (
    <Tooltip {...tooltipProps}>
      <TooltipTrigger asChild={triggerAsChild}>{trigger}</TooltipTrigger>
      <TooltipContent
        align={align}
        side={side}
        sideOffset={sideOffset}
        className={cn(
          'max-w-[20rem] px-3 py-2.5',
          contentClassName
        )}
      >
        {resolvedContent}
      </TooltipContent>
    </Tooltip>
  );
}
